import { useState, useContext } from 'react';
import { Box, Chip, Tooltip, Grid, Link } from '@mui/material';
import { format } from 'date-fns';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { getDateLocale } from '../../../lib/dateLocale';
import { PaymentOutlined, Replay } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttSelectableItems,
    SttFeeRow,
    SttChipPForm,
} from '../../../sporttia/all';
import {
    fullName,
    formatPriceByLocale,
    isDefined,
    safeFormatDate,
} from '../../../lib/utils';
import PaymentModal from '../../../components/paymentModal/PaymentModal';
import StudentDialog from '../../../components/dialogs/StudentDialog';
import { useInteractionsFiles, useQueryFilters } from '../../../lib/hooks';
import SttMshipDialog from '../../../sporttia/dialogs/SttMshipDialog';
import { SPORTCENTER_ACTIVITIES_STUDENTS } from '../../../lib/queryKeys';
import useActivityRatesService from '../../../services/ActivityRatesService';
import SttCachedTable from '../../../sporttia/SttCachedTable';
import { SttTopFilteringControls } from '../../../sporttia/SttTopFilteringControls';
import DiscountTooltip from '../../../components/discounts/DiscountTooltip';
import DateBuilder from '../../../lib/DateBuilder';
import type { Activity } from '../../../types/models/Activity';
import type { Discount } from '../../../types/models/Discount';
import { PaymentForm } from '../../../types/payment';
import { PERMISSIONS } from '../../../lib/permissions';

type Props = {
    item: Activity & { form?: ActivityFormQuestion[] };
    onSave: () => void;
    onPayment: () => void;
    onGenerateFee: () => void;
};

type ActivityFormQuestion = {
    id: number;
    name: string;
    type: string;
    typeLang: string;
    mandatory: boolean;
};

type Student = {
    id: number;
    name?: string;
    user: {
        id: number;
        name?: string;
        surname1?: string;
        surname2?: string;
        mship?: {
            id: number;
            fullName?: string;
            name?: string;
            surname1?: string;
            surname2?: string;
        };
    };
    status?: string;
    created?: string;
    form?: FormItem[];
    enrollment?: StudentPayment;
    rate: {
        enrollment: number;
    };
    discounts?: Discount[];
    ini?: string;
    end?: string;
    fees?: Fee[];
    feePaymentForm?: PaymentForm;
};

type FormItem = {
    id: number;
    name?: string;
    type: string;
    answer?: {
        value: string;
    };
};

type StudentPayment = {
    id: number;
    price: number;
    paymentForm: PaymentForm;
    refund?: boolean;
    user: {
        id: number;
    };
    children?: {
        rows?: StudentPayment[];
    };
};

type Fee = {
    id?: number;
    ini: string;
    price: number;
    status?: string;
    trash?: boolean;
    moment?: string;
    paymentForm?: string;
    feeslot?: {
        id: number;
    };
    priceShape?: {
        currency?: string;
        locale?: string;
    };
};

type Filters = {
    rate?: number;
    name?: string;
    inTime?: boolean | null;
    trash?: boolean;
};

export default function ActivityStudents({
    item,
    onSave,
    onPayment,
    onGenerateFee,
}: Props): React.ReactElement | null {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { downloadFile } = useInteractionsFiles();
    const activityRateService = useActivityRatesService();
    const [idPayModal, setIdPayModal] = useState<number | null>(null);
    const [viewStudentId, setViewStudentId] = useState<number | null>(null);
    const [mode, setMode] = useState<'STUDENTS' | 'FEES'>('STUDENTS');
    const [selectedMshipId, setSelectedMshipId] = useState<number | null>(null);
    const queryClient = useQueryClient();
    const [filters, setFilters] = useQueryFilters<Filters>(
        {},
        SPORTCENTER_ACTIVITIES_STUDENTS,
    );

    const activeFilter = 'ACTIVE_AND_FUTURE';
    const idRate = item?.rates?.[0]?.id ?? null;

    const queryKey = [SPORTCENTER_ACTIVITIES_STUDENTS, filters];

    function generateEnrollment(studentId: number): void {
        cxt?.api('POST', `/students/${studentId}/enrollment/generate`, {
            success: () => {
                cxt?.showMessage('S', t('GeneratedEnrollment'));
                queryClient.invalidateQueries({
                    queryKey: [SPORTCENTER_ACTIVITIES_STUDENTS],
                });
            },
        });
    }

    function updateMship(mship: { id: number }): void {
        queryClient.setQueryData<{ rows: Student[] }>(queryKey, ((
            old: { rows: Student[] } | undefined,
        ): { rows: Student[] } | undefined => {
            if (!old) {
                return old;
            }

            return {
                ...old,
                rows: old.rows.map((row: Student): Student => {
                    if (row.user?.mship?.id === mship.id) {
                        return {
                            ...row,
                            user: {
                                ...row.user,
                                mship: {
                                    ...row.user.mship,
                                    ...mship,
                                },
                            },
                        };
                    }

                    return row;
                }),
            };
        }) as any);
    }

    function onFeeGenerated(): void {
        queryClient.invalidateQueries({
            queryKey: [SPORTCENTER_ACTIVITIES_STUDENTS],
        });
    }

    if (!item) {
        return null;
    }

    let tableColumns;

    if (mode === 'STUDENTS') {
        tableColumns = [
            {
                title: t('Name'),
                align: 'left',
                field: 'name',
                value: (row: Student) => (
                    <Box
                        style={{
                            flexGrow: 1,
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <Link
                            onClick={() => setViewStudentId(row.id)}
                            sx={{
                                color: 'var(--brand-text)',
                                textDecoration: 'none',
                                cursor: 'pointer',
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            {fullName(row.user)}
                        </Link>
                        {row?.status === 'PAYING' && (
                            <Tooltip title={t('PayingWithCreditCard')}>
                                <PaymentOutlined
                                    style={{ marginLeft: 10, fontSize: 13 }}
                                />
                            </Tooltip>
                        )}
                    </Box>
                ),
            },
            {
                title: t('Created'),
                field: 'created',
                value: (row: Student) =>
                    new DateBuilder(
                        row?.created,
                        cxt?.sc?.timezone?.name,
                        cxt?.lang?.key,
                    ).dmy(),
            },
            {
                title: t('Form'),
                value: (row: Student) =>
                    row.form?.map(
                        (rowFormItem) =>
                            rowFormItem.answer?.value && (
                                <Tooltip
                                    arrow
                                    key={rowFormItem.id}
                                    placement="top"
                                    title={rowFormItem.name || ''}
                                    style={{
                                        marginLeft: 4,
                                        marginRight: 4,
                                        marginTop: 2,
                                        marginBottom: 2,
                                    }}
                                >
                                    <Chip
                                        size="small"
                                        label={
                                            rowFormItem.type === 'DATE'
                                                ? safeFormatDate(
                                                      rowFormItem.answer.value,
                                                      'dd-MM-yyyy',
                                                      {
                                                          locale: getDateLocale(),
                                                      },
                                                  )
                                                : rowFormItem.answer.value
                                        }
                                    />
                                </Tooltip>
                            ),
                    ),
            },
            {
                width: 165,
                title: t('Enrollment'),
                value: (row: Student) => {
                    let value;
                    if (row.enrollment) {
                        if (row.enrollment.refund) {
                            value = (
                                <Box
                                    px={1}
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <Link
                                        onClick={() =>
                                            setIdPayModal(row.enrollment!.id)
                                        }
                                        sx={{
                                            color: 'var(--brand-text)',
                                            textDecoration: 'none',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {formatPriceByLocale(
                                            row.enrollment.price,
                                            cxt?.sc?.city?.country?.currency,
                                        )}
                                    </Link>
                                    <Box mx={1}>
                                        <SttChipPForm
                                            pf={row.enrollment.paymentForm}
                                        />
                                    </Box>
                                    <Replay fontSize="small" />
                                </Box>
                            );
                        } else {
                            value = (
                                <Box
                                    px={1}
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <Link
                                        onClick={() =>
                                            setIdPayModal(row.enrollment!.id)
                                        }
                                        sx={{
                                            color: 'var(--brand-text)',
                                            textDecoration: 'none',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {formatPriceByLocale(
                                            row.enrollment.price,
                                            cxt?.sc?.city?.country?.currency,
                                        )}
                                    </Link>
                                    <Box mx={1}>
                                        <SttChipPForm
                                            pf={row.enrollment.paymentForm}
                                        />
                                    </Box>
                                    <Box width={20} height={20} />
                                </Box>
                            );
                        }
                    } else if (row.rate.enrollment > 0) {
                        value = (
                            <Box
                                px={1}
                                display="flex"
                                justifyContent="space-around"
                            >
                                <Link
                                    onClick={() => generateEnrollment(row.id)}
                                    sx={{
                                        color: 'var(--brand-text)',
                                        textDecoration: 'none',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                >
                                    {t('Generate')}
                                </Link>
                            </Box>
                        );
                    }

                    return value;
                },
            },
            {
                title: '',
                value: (row: Student) =>
                    row.discounts && (
                        <DiscountTooltip discounts={row.discounts} />
                    ),
            },
            {
                title: t('Period'),
                type: 'period',
                value: (row: Student) => ({ ini: row.ini, end: row.end }),
            },
            {
                title: t('Fees'),
                value: (row: Student) => {
                    if (row.fees?.length) {
                        return <SttChipPForm pf={row.feePaymentForm} />;
                    }
                    return null;
                },
            },
            {
                title: '',
                value: (row: Student) => (
                    <PersonOutlineOutlinedIcon
                        fontSize="small"
                        onClick={() => {
                            setSelectedMshipId(row?.user?.mship?.id ?? null);
                        }}
                    />
                ),
            },
        ];
    } else if (mode === 'FEES') {
        tableColumns = [
            {
                value: (row: Student) => (
                    <div>
                        <Box textAlign="left" mb={1}>
                            <Link
                                onClick={() => setViewStudentId(row.id)}
                                sx={{
                                    color: 'var(--brand-text)',
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                {row.user ? fullName(row.user) : row.name}
                            </Link>
                        </Box>
                        <SttFeeRow
                            fees={row.fees}
                            showUntil={format(
                                new Date(),
                                'yyyy-12-31:23:59:59',
                            )}
                            onGenerated={() => {
                                onGenerateFee();
                                onFeeGenerated();
                            }}
                            onFeeClicked={(id?: number) =>
                                id && setIdPayModal(id)
                            }
                        />
                    </div>
                ),
            },
        ];
    }

    function download(format: 'pdf' | 'csv'): void {
        const formatedFilters = { ...filters };
        const type = format === 'pdf' ? 'application/pdf' : 'text/csv';

        if (!isDefined(formatedFilters.name)) {
            formatedFilters.name = '';
        }
        const { rate: rateId } = formatedFilters;
        const inTime = !!(
            (formatedFilters as { onlyActive?: boolean; onTime?: boolean })
                .onlyActive ||
            (formatedFilters as { onlyActive?: boolean; onTime?: boolean })
                .onTime
        );

        delete formatedFilters.rate;
        // Migración a formato inTime,inPast,inFuture https://wiki.sporttia.com/index.php/Gesti%C3%B3n_de_fechas,_tiempos_y_zonas_horarias
        delete (formatedFilters as { onlyActive?: boolean }).onlyActive;
        delete (formatedFilters as { onTime?: boolean }).onTime;

        downloadFile(
            `/activities/rates/${rateId}/students.${format}`,
            type,
            { inTime, ...formatedFilters },
            `${item.name} - ${t('Students')}.${format}`,
        );
    }

    return (
        <>
            <SttTopFilteringControls
                mb={2}
                fields={[
                    {
                        name: 'rate',
                        type: 'select',
                        mandatory: true,
                        caption: t('Rate'),
                        value: idRate,
                        options: item.rates?.map((rate: any) => ({
                            caption: rate.name,
                            value: rate.id,
                        })),
                    },
                    {
                        name: 'activeFilter',
                        type: 'select',
                        mandatory: true,
                        caption: t('Active'),
                        value: activeFilter,
                        options: [
                            {
                                caption: t(
                                    'page.sc.classes.onlyActiveStudents',
                                ),
                                value: 'ACTIVE_AND_FUTURE',
                            },
                            {
                                caption: t(
                                    'page.sc.classes.activeAndPastStudents',
                                ),
                                value: 'PAST',
                            },
                        ],
                    },
                    {
                        name: 'name',
                        type: 'search',
                        caption: t('Name'),
                        value: filters.name,
                    },
                ]}
                menu={[
                    {
                        caption: `${t('Download')} PDF`,
                        onClick: () => download('pdf'),
                    },
                    {
                        caption: `${t('Download')} CSV`,
                        onClick: () => download('csv'),
                    },
                ]}
                mainAction={
                    mode === 'STUDENTS'
                        ? {
                              type: 'create',
                              permission: PERMISSIONS.ACTIVITY.MODIFY,
                              onClick: () => setViewStudentId(0),
                          }
                        : null
                }
                onFilter={({
                    activeFilter: currentFilter,
                    ...restFilters
                }: { activeFilter?: string } & Partial<Filters>) => {
                    let inTime: boolean | null = null;

                    if (idRate) {
                        if (currentFilter === 'ACTIVE_AND_FUTURE') {
                            inTime = true;
                        } else if (currentFilter === 'PAST') {
                            inTime = false;
                        }
                    }

                    setFilters({ ...restFilters, inTime } as Filters);
                }}
                trashAction
            />
            {/* --- Students or fees--- */}
            <Box pl={3}>
                <Grid container spacing={3}>
                    <SttSelectableItems
                        grid
                        xs={12}
                        md={4}
                        lg={3}
                        xl={2}
                        name="mode"
                        items={[
                            {
                                label: t('Students'),
                                value: 'STUDENTS',
                            },
                            {
                                label: t('Fees'),
                                value: 'FEES',
                            },
                        ]}
                        exclusive
                        selected={mode}
                        onChange={({ value }: { value: 'STUDENTS' | 'FEES' }) =>
                            setMode(value)
                        }
                    />
                </Grid>
            </Box>
            <SttCachedTable
                queryKey={SPORTCENTER_ACTIVITIES_STUDENTS}
                queryFn={({ rate: id, ...params }: { rate?: number }) =>
                    activityRateService.getActivityRateStudents(id!, params)
                }
                queryParams={{ ...filters }}
                prefetching={false}
                columns={tableColumns as any}
            />
            {idPayModal && (
                <PaymentModal
                    idPayment={idPayModal}
                    onClose={() => setIdPayModal(null)}
                    onPay={() => {
                        queryClient.invalidateQueries({
                            queryKey: [SPORTCENTER_ACTIVITIES_STUDENTS],
                        });
                        onPayment();
                    }}
                    onDeleted={() => {
                        queryClient.invalidateQueries({
                            queryKey: [SPORTCENTER_ACTIVITIES_STUDENTS],
                        });
                        onPayment();
                    }}
                    onRecovered={() => {
                        queryClient.invalidateQueries({
                            queryKey: [SPORTCENTER_ACTIVITIES_STUDENTS],
                        });
                        onPayment();
                    }}
                    onRefund={() => {
                        queryClient.invalidateQueries({
                            queryKey: [SPORTCENTER_ACTIVITIES_STUDENTS],
                        });
                        onPayment();
                    }}
                    onSave={() => {
                        queryClient.invalidateQueries({
                            queryKey: [SPORTCENTER_ACTIVITIES_STUDENTS],
                        });
                        onPayment();
                    }}
                />
            )}
            {viewStudentId !== null && (
                <StudentDialog
                    open
                    idStudent={viewStudentId}
                    defaultIdRate={filters.rate ?? idRate}
                    activity={item}
                    onClose={() => {
                        setViewStudentId(null);
                    }}
                    onSave={() => {
                        setViewStudentId(null);
                        queryClient.invalidateQueries({
                            queryKey: [SPORTCENTER_ACTIVITIES_STUDENTS],
                        });
                        onSave();
                    }}
                    trash={filters.trash}
                />
            )}
            <SttMshipDialog
                open={!!selectedMshipId}
                mshipId={selectedMshipId}
                onClose={() => {
                    setSelectedMshipId(null);
                }}
                onSave={updateMship}
            />
        </>
    );
}
